import { ApiError } from '@/api'

export type ErrorKind = 'user' | 'dev'

export interface NormalizedApiError {
  status: number | undefined
  message: string
  technical: string
  kind: ErrorKind
  code?: string
}

/**
 * Extracts the most specific user-facing message from any thrown value.
 *
 * Priority:
 *   1. FastAPI `body.detail` string (business-logic errors, e.g. "Email already in use")
 *   2. `classifyApiError` message for ApiError (friendly HTTP-level message)
 *   3. `err.message` for plain Error instances (network errors, etc.)
 *   4. `classifyApiError` message for status-bearing plain objects
 */
export function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = (err.body as { detail?: unknown } | null | undefined)?.detail
    if (typeof detail === 'string' && detail) return detail
    if (detail && typeof detail === 'object') {
      const msg = (detail as { message?: unknown }).message
      if (typeof msg === 'string' && msg) return msg
    }
    return classifyApiError(err).message
  }
  if (err instanceof Error) return err.message || 'Request failed'
  return classifyApiError(err).message
}

// For these status codes the HTTP-level message from the server is always
// terse/technical (e.g. "Forbidden", "Unauthorized"), so we replace it.
const FRIENDLY_MESSAGES: Partial<Record<number, string>> = {
  401: 'Unauthorized.',
  403: "You don't have permission to do this.",
  404: "The item you're looking for wasn't found.",
}

const DEV_MESSAGE = 'Something went wrong on our end. Try refreshing the page.'

interface ErrorMetadata {
  status: number | undefined
  technical: string
  code?: string
  detailMessage?: string
}

function detailMetadata(detail: unknown): Pick<ErrorMetadata, 'code' | 'detailMessage'> {
  if (typeof detail === 'string' && detail) return { detailMessage: detail }
  if (!detail || typeof detail !== 'object') return {}

  const { code, message } = detail as { code?: unknown; message?: unknown }
  return {
    ...(typeof code === 'string' ? { code } : {}),
    ...(typeof message === 'string' && message ? { detailMessage: message } : {}),
  }
}

function errorMetadata(err: unknown): ErrorMetadata {
  if (err instanceof ApiError) {
    return {
      status: err.status,
      technical: err.message || err.statusText || 'Request failed',
      ...detailMetadata((err.body as { detail?: unknown } | null | undefined)?.detail),
    }
  }
  if (!err || typeof err !== 'object') {
    return { status: undefined, technical: 'Request failed' }
  }

  const value = err as {
    status?: unknown
    message?: unknown
    code?: unknown
    body?: { detail?: unknown }
  }
  const detail = detailMetadata(value.body?.detail)
  return {
    status: typeof value.status === 'number' ? value.status : undefined,
    technical: typeof value.message === 'string' ? value.message : 'Request failed',
    ...detail,
    ...(!detail.code && typeof value.code === 'string' ? { code: value.code } : {}),
  }
}

/**
 * Normalises any thrown value from the generated API client into a
 * user-presentable message, a raw technical string, and a kind tag.
 *
 * - kind='user'  → a 4xx the user can act on (wrong input, no permission, etc.)
 * - kind='dev'   → a 5xx or network failure outside the user's control
 */
export function classifyApiError(err: unknown): NormalizedApiError {
  const { status, technical, code, detailMessage } = errorMetadata(err)

  const kind: ErrorKind = !status || status >= 500 ? 'dev' : 'user'

  const message =
    kind === 'dev' ? DEV_MESSAGE : (detailMessage ?? FRIENDLY_MESSAGES[status!] ?? technical)

  return { status, message, technical, kind, ...(code !== undefined ? { code } : {}) }
}
